import { Palette, useModal } from "@homebound/beam";
import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { ChangeEventWorkflowComments } from "src/routes/projects/change-events/guided/components/ChangeEventWorkflowComments";
import { changeEventQueryFor, GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { useReviewStepActions } from "src/routes/projects/change-events/guided/workflows/sharedSteps/useReviewStepActions";
import { defineWorkflow } from "src/routes/projects/change-events/guided/workflows/types";
import { SelectConfigurationStep } from "../1.SelectConfigurationStep";
import {
  applyConfigurationFormConfig,
  ApplyConfigurationFormInput,
  applyConfigurationMapToForm,
} from "../applyConfigurationForm";
import { ApplyConfigurationReviewStep } from "../components/ApplyConfigurationReviewStep";
import { ReviewConfigurationStep } from "../ReviewConfigurationStep";
import { ReviewImpactStep } from "../ReviewImpactStep";
import { useApplyConfigurationDirectly } from "../useApplyConfigurationDirectly";
import { useApplyConfigurationActions } from "../useSaveApplyConfigurationChangeEvent";
import { ApplyInitialConfigurationGate } from "./components/ApplyInitialConfigurationGate";

export const applyInitialConfigurationWorkflow = defineWorkflow<ApplyConfigurationFormInput>({
  workflow: ChangeEventWorkflow.ApplyInitialConfiguration,
  formConfig: applyConfigurationFormConfig,
  gate: (props) => <ApplyInitialConfigurationGate {...props} />,
  mapToForm: applyConfigurationMapToForm,
  steps: [
    {
      value: GuidedChangeEventStep.SelectConfiguration,
      label: "Select Configuration",
      isComplete: (form) => !!form.targetConfigId.value && form.optionIds.valid,
      render: (form, project) => <SelectConfigurationStep formState={form} project={project} />,
    },
    // Every review reads the change event step 1 saved (or dry ran), so none can open until that succeeded
    {
      value: GuidedChangeEventStep.ReviewConfiguration,
      label: "Review Configuration",
      isComplete: (form) => !!form.targetConfigId.value,
      disabled: (form) => !changeEventQueryFor(form).changeEventId,
      render: (form) => <ReviewConfigurationStep formState={form} />,
    },
    {
      value: GuidedChangeEventStep.ReviewImpact,
      label: "Review Impact",
      isComplete: (form) => !!form.targetConfigId.value,
      disabled: (form) => !changeEventQueryFor(form).changeEventId,
      render: (form) => <ReviewImpactStep formState={form} />,
    },
    {
      value: GuidedChangeEventStep.Review,
      label: "Review Change Event",
      isComplete: (form) => form.valid,
      disabled: (form) => !changeEventQueryFor(form).changeEventId,
      render: (form) => <ApplyConfigurationReviewStep formState={form} />,
      // Without a signed contract step 1 only dry ran the save, so there's no change event to comment on
      rightPaneTriggers: (form) =>
        form.id.value
          ? [
              {
                icon: "comment",
                label: "Comments",
                content: <ChangeEventWorkflowComments changeEventId={form.id.value} />,
              },
            ]
          : [],
    },
  ],
  // Resuming a saved change event whose configuration is already picked starts on its impact, like swap product.
  initialStep: ({ form }) =>
    form.id.value && form.targetConfigId.value && form.optionIds.valid
      ? GuidedChangeEventStep.ReviewImpact
      : GuidedChangeEventStep.SelectConfiguration,
  useActions: (args) => {
    const { form, project } = args;
    const { openModal } = useModal();
    const actions = useApplyConfigurationActions(args);
    const { submit } = useReviewStepActions(args);
    const applyDirectly = useApplyConfigurationDirectly(args);
    const hasSignedContract = !!project.latestActiveStage?.hasSignedContract;

    return {
      ...actions,
      onSubmit: async () => {
        // A draft change event already exists, so the configuration is routed for approval instead of
        // replaced right now, and there is nothing destructive to warn about yet.
        if (form.value.id) {
          await submit();
          return;
        }

        openModal({
          content: (
            // Show a warning modal before submitting since this is a destructive action
            <ConfirmationModal
              title="Warning: Replace Existing Configuration"
              icon={{ icon: "error", color: Palette.Orange600 }}
              confirmationMessage={
                hasSignedContract
                  ? "Applying a full configuration replaces the project's existing configuration and scopes. Are you sure you want to continue?"
                  : "Applying a full configuration replaces the project's existing configuration and scopes. This will be applied immediately and no Change Event will be created. Are you sure you want to continue?"
              }
              label={hasSignedContract ? "Submit" : "Apply"}
              onConfirmAction={hasSignedContract ? submit : applyDirectly}
            />
          ),
        });
      },
    };
  },
});
